import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, MessageSquareText } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Por que o atendimento acabou.
//
// A chave é curta e estável; o rótulo é só o que se lê. Trocar o texto aqui
// não pode invalidar o que já foi gravado — por isso os dois são separados.
const MOTIVOS = [
	{
		chave: "resolvido",
		rotulo: "Resolvido",
		ajuda: "O cliente conseguiu o que precisava.",
	},
	{
		chave: "sem_retorno",
		rotulo: "Sem retorno",
		ajuda: "A pessoa parou de responder no meio.",
	},
	{
		chave: "duplicado",
		rotulo: "Duplicado",
		ajuda: "Mesma conversa já atendida em outro lugar.",
	},
	{
		chave: "nao_era_atendimento",
		rotulo: "Não era atendimento",
		ajuda: "Engano, spam ou mensagem sem pedido.",
	},
];

// Modal do botão "Resolver".
//
// Encerrar era um clique só e sem volta. Aqui a pessoa confirma, diz por que
// está encerrando e decide se pergunta ao cliente antes — quem já ouviu
// "obrigado, resolvido" não precisa perguntar de novo.
const ResolverTicketModal = ({ open, onClose, ticket, onResolved }) => {
	const [motivo, setMotivo] = useState("resolvido");
	const [observacao, setObservacao] = useState("");
	const [perguntarAoCliente, setPerguntarAoCliente] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [conexaoPergunta, setConexaoPergunta] = useState(false);

	// Grupo não tem "o cliente": qualquer um responderia pelos outros.
	const ehGrupo = Boolean(ticket?.isGroup);

	useEffect(() => {
		if (!open) return;

		setMotivo("resolvido");
		setObservacao("");

		// A conexão já pede confirmação? Então o padrão do modal acompanha —
		// quem configurou isso não quer desmarcar a cada encerramento.
		(async () => {
			if (!ticket?.whatsappId || ehGrupo) {
				setConexaoPergunta(false);
				setPerguntarAoCliente(false);
				return;
			}

			try {
				const { data } = await api.get(`/whatsapp/${ticket.whatsappId}`);
				const liga = Boolean(data?.confirmCloseEnabled);
				setConexaoPergunta(liga);
				setPerguntarAoCliente(liga);
			} catch {
				setConexaoPergunta(false);
				setPerguntarAoCliente(false);
			}
		})();
	}, [open, ticket?.whatsappId, ehGrupo]);

	const resolver = async () => {
		setSalvando(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: "closed",
				userId: ticket.userId,
				closeReason: motivo,
				closeNote: observacao.trim() || undefined,
				pedirConfirmacao: perguntarAoCliente,
			});

			toast.success(
				perguntarAoCliente
					? "Pedimos a confirmação ao cliente."
					: "Atendimento encerrado.",
			);

			if (onResolved) onResolved();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Encerrar atendimento</DialogTitle>
					<DialogDescription>
						{ticket?.contact?.name
							? `Conversa com ${ticket.contact.name}.`
							: "O atendimento sai da sua lista."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label>Por que está encerrando?</Label>
						<div className="grid grid-cols-2 gap-2">
							{MOTIVOS.map((m) => (
								<button
									key={m.chave}
									type="button"
									onClick={() => setMotivo(m.chave)}
									className={cn(
										"rounded-lg border p-2.5 text-left transition-colors",
										motivo === m.chave
											? "border-primary bg-primary/5"
											: "hover:bg-muted",
									)}
								>
									<span className="block text-sm font-medium">{m.rotulo}</span>
									<span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
										{m.ajuda}
									</span>
								</button>
							))}
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="closeNote">Observação (opcional)</Label>
						<Textarea
							id="closeNote"
							rows={2}
							value={observacao}
							onChange={(e) => setObservacao(e.target.value)}
							placeholder="Algo que a equipe precise saber depois."
						/>
						<p className="text-xs text-muted-foreground">
							Fica no atendimento, para a equipe. O cliente não vê.
						</p>
					</div>

					{!ehGrupo && (
						<div className="flex items-start justify-between gap-4 rounded-lg border p-3">
							<div className="space-y-0.5">
								<Label
									htmlFor="perguntarAoCliente"
									className="flex items-center gap-1.5 text-sm"
								>
									<MessageSquareText className="h-3.5 w-3.5" />
									Perguntar ao cliente antes
								</Label>
								<p className="text-xs leading-relaxed text-muted-foreground">
									Manda um botão “Finalizar atendimento” e só encerra quando ele
									confirmar. O atendimento continua aberto até lá.
									{!conexaoPergunta && (
										<>
											{" "}
											<span className="text-amber-600 dark:text-amber-500">
												Esta conexão não está configurada para isso — ligue em
												Conexões para usar seus próprios textos.
											</span>
										</>
									)}
								</p>
							</div>
							<Switch
								id="perguntarAoCliente"
								checked={perguntarAoCliente}
								onCheckedChange={setPerguntarAoCliente}
								disabled={!conexaoPergunta}
							/>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={resolver} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{perguntarAoCliente ? "Perguntar e aguardar" : "Encerrar agora"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ResolverTicketModal;
